import React, { createContext, useContext, useState, useEffect } from 'react';

const ThemeContext = createContext();

const THEME_STORAGE_KEY = 'tripdata_theme_mode';

// Colors matching the mobile app exactly
export const colors = {
  light: {
    // Backgrounds
    background: '#f5f5f5',
    card: '#fff',
    cardAlt: '#f8f8f8',
    
    // Text
    text: '#333',
    textSecondary: '#666',
    textMuted: '#999',
    textPlaceholder: '#999',
    
    // Borders
    border: '#eee',
    borderLight: '#f0f0f0',
    divider: '#e0e0e0',
    
    // Status
    statusInactive: '#ccc',
    
    // Badges
    badgeBackground: '#f0f0f0',
    badgeText: '#999',
    businessBadge: '#e1f5fe',
    businessText: '#0277bd',
    privateBadge: '#fce4ec',
    privateText: '#c2185b',
    
    // Buttons
    buttonDisabled: '#e8e8e8',
    buttonDisabledText: '#666',
    
    // Icons
    iconDefault: '#666',
    iconMuted: '#ccc',
    chevron: '#ccc',
    
    // Shadows
    shadowColor: 'rgba(0, 0, 0, 0.1)',
    
    // Overlay
    overlay: 'rgba(0, 0, 0, 0.5)',
  },
  dark: {
    // Backgrounds - mid grey like the app
    background: '#616161',
    card: '#2f2f2f',
    cardAlt: '#2f2f2f',
    
    // Text
    text: '#f5f5f5',
    textSecondary: '#bbb',
    textMuted: '#888',
    textPlaceholder: '#666',
    
    // Borders
    border: '#333',
    borderLight: '#2a2a2a',
    divider: '#444',
    
    // Status
    statusInactive: '#555',
    
    // Badges
    badgeBackground: '#2a2a2a',
    badgeText: '#888',
    businessBadge: '#0d3347',
    businessText: '#4fc3f7',
    privateBadge: '#3d1f2a',
    privateText: '#f48fb1',
    
    // Buttons
    buttonDisabled: '#333',
    buttonDisabledText: '#888',
    
    // Icons
    iconDefault: '#bbb',
    iconMuted: '#555',
    chevron: '#555',
    
    // Shadows
    shadowColor: 'rgba(0, 0, 0, 0.3)',
    
    // Overlay
    overlay: 'rgba(0, 0, 0, 0.7)',
  },
  
  // Shared colors (same in both themes)
  shared: {
    // Brand gradient
    gradientStart: '#00BCD4',
    gradientEnd: '#4CAF50',
    
    // Primary accent
    primary: '#00ACC1',
    primaryDark: '#00838f',
    
    // Stop/Delete
    danger: '#f44336',
    dangerDark: '#d32f2f',
    
    // Success
    success: '#16a34a',
    
    // White
    white: '#fff',
    
    // Business/Private colors
    business: '#FF9800',
    private: '#4CAF50',
  }
};

export const ThemeProvider = ({ children }) => {
  const [mode, setModeState] = useState('system');
  const [isLoaded, setIsLoaded] = useState(false);

  // Check system preference
  const getSystemTheme = () => {
    if (typeof window !== 'undefined') {
      return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    }
    return 'light';
  };

  // Load saved preference on mount
  useEffect(() => {
    const saved = localStorage.getItem(THEME_STORAGE_KEY);
    if (saved === 'light' || saved === 'dark' || saved === 'system') {
      setModeState(saved);
    }
    setIsLoaded(true);
  }, []);

  // Listen for system theme changes
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = () => {
      if (mode === 'system') {
        // Force re-render when system theme changes
        setModeState('system');
      }
    };
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [mode]);

  // Apply dark class to html element
  useEffect(() => {
    if (!isLoaded) return;
    
    const isDark = mode === 'system' ? getSystemTheme() === 'dark' : mode === 'dark';
    
    if (isDark) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [mode, isLoaded]);

  const setMode = (newMode) => {
    setModeState(newMode);
    localStorage.setItem(THEME_STORAGE_KEY, newMode);
  };

  const isDark = mode === 'system' ? getSystemTheme() === 'dark' : mode === 'dark';

  const theme = {
    ...(isDark ? colors.dark : colors.light),
    ...colors.shared,
    isDark,
  };

  if (!isLoaded) {
    return null;
  }

  return (
    <ThemeContext.Provider value={{ theme, mode, setMode, isDark }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context.theme;
};

export const useThemeMode = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useThemeMode must be used within ThemeProvider');
  }
  return { mode: context.mode, setMode: context.setMode, isDark: context.isDark };
};